import { useCallback, useEffect, useState } from 'react';
import { knowledgeAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import KBNavigationPanel from '@/components/kb/KBNavigationPanel';
import { FileText, FileCode, Ticket, FileType, RefreshCw } from 'lucide-react';

function IndexedFilesSidebar() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await knowledgeAPI.files();
      setFiles(res.data?.files || []);
    } catch {
      /* empty */
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onUpdate = () => load(true);
    window.addEventListener('kb-index-updated', onUpdate);
    return () => window.removeEventListener('kb-index-updated', onUpdate);
  }, [load]);

  const filtered = files.filter((f) =>
    !filter
    || (f.rel_path || '').toLowerCase().includes(filter.toLowerCase())
    || (f.mount_id || '').toLowerCase().includes(filter.toLowerCase()),
  );

  const colIcon = (t) => {
    if (t === 'code') return <FileCode className="w-3 h-3 text-blue-500" />;
    if (t === 'tickets') return <Ticket className="w-3 h-3 text-amber-500" />;
    return <FileType className="w-3 h-3 text-green-500" />;
  };

  if (loading) return <Skeleton className="h-[300px]" />;

  return (
    <Card data-testid="indexed-files-sidebar">
      <CardHeader className="py-3 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <FileText className="w-4 h-4" /> Registro indexado
        </CardTitle>
        <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => load()} disabled={loading} title="Actualizar">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filtrar por ruta o mount..."
          className="text-xs h-8"
        />
        <Badge variant="outline" className="text-[10px]">{filtered.length} archivos</Badge>
        <div className="max-h-[360px] overflow-y-auto space-y-1 text-xs">
          {filtered.length === 0 ? (
            <p className="text-zinc-400 p-2">Sin archivos indexados</p>
          ) : (
            filtered.slice(0, 200).map((f) => (
              <div key={f.index_key || f.rel_path} className="flex items-center gap-2 py-1 border-b border-zinc-100">
                {colIcon(f.collection_type)}
                <span className="truncate flex-1 font-mono text-[10px]">{f.rel_path}</span>
                {f.mount_id && <Badge className="text-[8px]">{f.mount_id}</Badge>}
                <span className="text-zinc-400">{f.chunks_indexed || 0}ch</span>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function KBContentPanel({ user }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="kb-content-panel">
      <div className="lg:col-span-2">
        <KBNavigationPanel user={user} />
      </div>
      <div>
        <IndexedFilesSidebar />
      </div>
    </div>
  );
}
